import { NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useLang } from "@/contexts/LanguageContext";
import { useShop } from "@/contexts/ShopContext";
import { api } from "@/lib/api";
import { NAV } from "@/constants/testIds";
import {
  LayoutDashboard,
  ListOrdered,
  Gauge,
  Users,
  Sparkles,
  Mail,
  Settings as SettingsIcon,
  Search,
  Link2,
} from "lucide-react";

const linkBase =
  "flex items-center gap-3 px-3 h-10 rounded-md text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors";
const linkActive = "bg-secondary text-foreground";

export default function Sidebar() {
  const { t } = useLang();
  const { selectedShopId } = useShop();

  const { data: summary = {} } = useQuery({
    queryKey: ["alerts-summary"],
    queryFn: async () => (await api.get("/alerts/summary")).data,
    refetchInterval: 60000,
  });

  const alertCount = summary?.[selectedShopId]?.total || 0;
  const hasCritical = (summary?.[selectedShopId]?.critical || 0) > 0;

  const items = [
    { to: "/dashboard", label: t("nav_dashboard"), icon: LayoutDashboard, testId: NAV.dashboardLink,
      alertBadge: alertCount > 0 ? { count: alertCount, critical: hasCritical } : null },
    { to: "/keywords", label: t("nav_keywords"), icon: ListOrdered, testId: NAV.keywordsLink },
    { to: "/audit", label: t("nav_audit"), icon: Gauge, testId: NAV.auditLink },
    { to: "/competitors", label: t("nav_competitors"), icon: Users, testId: NAV.competitorsLink },
    { to: "/backlinks", label: "Backlinks", icon: Link2, testId: "nav-backlinks" },
    { to: "/cro", label: t("nav_cro"), icon: Sparkles, testId: NAV.croLink, badge: "AI" },
    { to: "/search-console", label: t("nav_search_console"), icon: Search, testId: "nav-search-console" },
    { to: "/reports", label: t("nav_reports"), icon: Mail, testId: NAV.reportsLink },
    { to: "/settings", label: t("nav_settings"), icon: SettingsIcon, testId: NAV.settingsLink },
  ];

  return (
    <aside
      data-testid={NAV.sidebar}
      className="w-60 shrink-0 border-r border-border hidden md:flex flex-col"
    >
      <div className="h-16 px-5 flex items-center border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-foreground text-background grid place-items-center font-display font-bold">
            R
          </div>
          <div className="font-display text-lg font-semibold tracking-tight">Rankly</div>
        </div>
      </div>

      <nav className="flex-1 p-3 space-y-1">
        {items.map(({ to, label, icon: Icon, testId, badge, alertBadge }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testId}
            className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}
          >
            <Icon className="h-4 w-4" />
            <span>{label}</span>
            {alertBadge && (
              <span
                data-testid="nav-alert-badge"
                className={`ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded text-white ${
                  alertBadge.critical ? "bg-destructive" : "bg-[hsl(var(--chart-4))]"
                }`}
              >
                {alertBadge.count}
              </span>
            )}
            {badge && !alertBadge && (
              <span className="ml-auto text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-accent-action text-white">
                {badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-mono border-t border-border">
        v0.5 · Rankly
      </div>
    </aside>
  );
}
